import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import css from "./style.module.css";
import { useQuery } from '@tanstack/react-query';
import { checkComplaints } from '../LayOuts/Api';
import { isHqPortal } from '../../lib/hqPortal';
import { PORTAL_ORIGINS } from "../../lib/hosts";

// Constants
const COLLAPSED_WIDTH = '65px';
const EXPANDED_WIDTH = '220px';
const STORAGE_KEY = 'navbar-collapsed';




export default function NavBar() {
    const location = useLocation();
    const navigate = useNavigate();

    // 1. Derive User Team (Memoize or use state if this changes)
    const [userTeam] = useState(() => {
        const team = sessionStorage.getItem("Team") || (localStorage.getItem("Vendor User") ? "Vendor" : null);
        if (team === "Vendor" && !sessionStorage.getItem("Team")) {
            sessionStorage.setItem("Team", "Vendor");
        }
        return team;
    });

    // 2. Correct Hook Usage (Always at top level)
    const { data: hasComplaints, isLoading, error } = useQuery({
        queryKey: ['checkComplaints'],
        queryFn: checkComplaints,
        staleTime: 29 * 60 * 1000,
        enabled: !!userTeam && userTeam !== "Vendor", // Only fetch if user is not a vendor
    });

    const allowedRoles = ['admin', 'marketing', 'compliance', 'superAdmin'];

    const [isCollapsed, setIsCollapsed] = useState(() => {
        const saved = sessionStorage.getItem(STORAGE_KEY);
        return saved ? JSON.parse(saved) : false;
    });

    const companyLogo = localStorage.getItem("Company Logo");
    const { protocol, hostname, port } = window.location;
    const fullUrl = `${protocol}//${hostname}${port ? `:${port}` : ""}/`;
    const currentPath = location.pathname;

    // Helper functions
    const isMarketingEnabled = () => hostname.endsWith('.pirm.io') || PORTAL_ORIGINS.includes(fullUrl.replace(/\/$/, ''));

    const hasMarketingAccess = () => {
        return userTeam && allowedRoles.includes(userTeam);
    };

    const canManageUsers = () => {
        return userTeam && ['admin', 'superAdmin'].includes(userTeam);
    };

    const isPolicySection = () => {
        return ['policies', 'details', 'marketing', 'setup', 'adminpanel', 'internal-users', 'companies', 'assignments', 'assignment', 'complaints', 'admin'].some(
            path => currentPath.startsWith(`/${path}`)
        );
    };

    const isVendorSection = () => {
        return currentPath.startsWith('/vendors') || currentPath.startsWith('/questions');
    };

    const getLogoRedirectPath = () => {
        return isVendorSection() ? "/vendors" : "/policies";
    };

    // Event handlers
    const handleLogOut = async () => {
        // Clear all auth and session data
        const itemsToRemove = [
            "okta-token-storage",
            "okta-cache-storage",
            "User Email",
            "User Name",
            "Vendor User",
            "sessionId",
            "Firebase Auth"
        ];

        itemsToRemove.forEach(item => localStorage.removeItem(item));
        sessionStorage.removeItem('from');
        sessionStorage.removeItem("Team");

        const redirectPath = isVendorSection() ? "/vendorlogin" : "/";
        navigate(redirectPath);
    };

    const toggleNavbar = () => {
        const newState = !isCollapsed;
        setIsCollapsed(newState);
        sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newState));
        adjustMainContent(newState);
    };

    const adjustMainContent = (collapsed) => {
        const mainContent = document.getElementById('main-content');
        if (mainContent) {
            const width = collapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH;
            mainContent.style.marginLeft = width;
            mainContent.style.width = `calc(100% - ${width})`;
        }
    };

    // Effects
    useEffect(() => {
        adjustMainContent(isCollapsed);
    }, [isCollapsed]);

    // Render helpers
    const renderNavItem = (to, icon, label, isActiveCheck) => (
        <div className={`d-flex align-items-center my-1 ${css.navItem} ${isCollapsed ? css.collapsed : ''}`}>
            <NavLink
                to={to}
                className={({ isActive }) =>
                    (isActiveCheck ? isActiveCheck() : isActive)
                        ? `text-decoration-none ${css.navLink} ${css.active}`
                        : `text-decoration-none ${css.navLink} ${css.inactive}`
                }
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    width: '100%',
                    padding: isCollapsed ? '12px 8px' : '12px 14px',
                    borderRadius: '8px',
                    transition: 'all 0.2s ease'
                }}
            >
                <div className={css.iconContainer} style={{ paddingLeft: isCollapsed ? '0px' : "" }}>
                    {icon}
                </div>
                <span
                    className={`${css.navText} ${isCollapsed ? css.hidden : css.visible}`}
                    style={{
                        marginLeft: isCollapsed ? '0' : '12px',
                        opacity: isCollapsed ? 0 : 1,
                        transform: isCollapsed ? 'translateX(-10px)' : 'translateX(0)',
                        transition: 'all 0.3s ease',
                        whiteSpace: 'nowrap',
                        fontSize: '14px',
                        fontWeight: '500'
                    }}
                >
                    {label}
                </span>
            </NavLink>
        </div>
    );

    const renderToggleButton = () => (
        <button
            className={`btn border-0 ${css.toggleBtn} ${isCollapsed ? 'position-absolute' : ''}`}
            onClick={toggleNavbar}
            style={{
                background: 'transparent',
                padding: '6px',
                borderRadius: '6px',
                transition: 'all 0.2s ease',
                minWidth: '32px',
                ...(isCollapsed && {
                    top: '12px',
                    right: '50%',
                    transform: 'translateX(50%)'
                })
            }}
            aria-label={isCollapsed ? "Expand navigation" : "Collapse navigation"}
        >
            <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                style={{
                    color: 'var(--ink-soft)',
                    transition: 'transform 0.3s ease',
                    transform: isCollapsed ? 'rotate(180deg)' : 'none'
                }}
            >
                <path
                    d="M15.41 7.41L14 6L8 12L14 18L15.41 16.59L10.83 12L15.41 7.41Z"
                    fill="currentColor"
                />
            </svg>
        </button>
    );

    // Icons
    const PolicyIcon = () => (
        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5 3.667C5 2.95967 5.28099 2.28131 5.78115 1.78115C6.28131 1.28099 6.95967 1 7.667 1H16.333C16.6832 1 17.03 1.06898 17.3536 1.20301C17.6772 1.33704 17.9712 1.53349 18.2189 1.78115C18.4665 2.0288 18.663 2.32281 18.797 2.64638C18.931 2.96996 19 3.31676 19 3.667V12.333C19 12.6832 18.931 13.03 18.797 13.3536C18.663 13.6772 18.4665 13.9712 18.2189 14.2189C17.9712 14.4665 17.6772 14.663 17.3536 14.797C17.03 14.931 16.6832 15 16.333 15H7.667C6.95967 15 6.28131 14.719 5.78115 14.2189C5.28099 13.7187 5 13.0403 5 12.333V3.667Z"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M2.012 5.26C1.705 5.43443 1.44965 5.68702 1.2719 5.99211C1.09415 6.2972 1.00034 6.64391 1 6.997V16.997C1 18.097 1.9 18.997 3 18.997H13C13.75 18.997 14.158 18.612 14.5 17.997M9 5H14M9 8H15M9 11H12"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const MarketingIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M14 2V8H20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7 12L9 14L13 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M7 16L11 20M11 16L7 20" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M15 12H17M15 16H17" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const AssignmentIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" stroke="currentColor">
            <g id="SVGRepo_bgCarrier" strokeWidth="0"></g>
            <g id="SVGRepo_tracerCarrier" strokeLinecap="round" strokeLinejoin="round"></g>
            <g id="SVGRepo_iconCarrier"> <path d="M2.75458 14.716L3.27222 16.6479C3.87647 18.9029 4.17859 20.0305 4.86351 20.7618C5.40432 21.3392 6.10421 21.7433 6.87466 21.9229C7.85044 22.1504 8.97798 21.8483 11.2331 21.244C13.4881 20.6398 14.6157 20.3377 15.347 19.6528C15.4077 19.5959 15.4664 19.5373 15.5233 19.477C15.1891 19.449 14.852 19.3952 14.5094 19.3271C13.8133 19.1887 12.9862 18.967 12.008 18.7049L11.9012 18.6763L11.8764 18.6697C10.8121 18.3845 9.92281 18.1457 9.21277 17.8892C8.46607 17.6195 7.7876 17.287 7.21148 16.7474C6.41753 16.0038 5.86193 15.0414 5.61491 13.982C5.43567 13.2133 5.48691 12.4594 5.62666 11.6779C5.76058 10.929 6.00109 10.0315 6.28926 8.95613L6.28926 8.95611L6.82365 6.96174L6.84245 6.8916C4.9219 7.40896 3.91101 7.71505 3.23687 8.34646C2.65945 8.88726 2.25537 9.58715 2.07573 10.3576C1.84821 11.3334 2.15033 12.4609 2.75458 14.716Z" fill="currentColor"></path>
                <path fillRule="evenodd" clipRule="evenodd" d="M20.8293 10.7154L20.3116 12.6473C19.7074 14.9024 19.4052 16.0299 18.7203 16.7612C18.1795 17.3386 17.4796 17.7427 16.7092 17.9223C16.6129 17.9448 16.5152 17.9621 16.415 17.9744C15.4999 18.0873 14.3834 17.7881 12.3508 17.2435C10.0957 16.6392 8.96815 16.3371 8.23687 15.6522C7.65945 15.1114 7.25537 14.4115 7.07573 13.641C6.84821 12.6652 7.15033 11.5377 7.75458 9.28263L8.27222 7.35077C8.3591 7.02654 8.43979 6.7254 8.51621 6.44561C8.97128 4.77957 9.27709 3.86298 9.86351 3.23687C10.4043 2.65945 11.1042 2.25537 11.8747 2.07573C12.8504 1.84821 13.978 2.15033 16.2331 2.75458C18.4881 3.35883 19.6157 3.66095 20.347 4.34587C20.9244 4.88668 21.3285 5.58657 21.5081 6.35703C21.7356 7.3328 21.4335 8.46034 20.8293 10.7154ZM11.0524 9.80589C11.1596 9.40579 11.5709 9.16835 11.971 9.27556L16.8006 10.5697C17.2007 10.6769 17.4381 11.0881 17.3309 11.4882C17.2237 11.8883 16.8125 12.1257 16.4124 12.0185L11.5827 10.7244C11.1826 10.6172 10.9452 10.206 11.0524 9.80589ZM10.2756 12.7033C10.3828 12.3032 10.794 12.0658 11.1941 12.173L14.0919 12.9495C14.492 13.0567 14.7294 13.4679 14.6222 13.868C14.515 14.2681 14.1038 14.5056 13.7037 14.3984L10.8059 13.6219C10.4058 13.5147 10.1683 13.1034 10.2756 12.7033Z" fill="currentColor"></path>
            </g>
        </svg>
    );

    const VendorIcon = () => (
        <svg width="16" height="20" viewBox="0 0 16 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M13.751 11.995C14.3474 11.9955 14.9192 12.2328 15.3407 12.6547C15.7622 13.0766 15.999 13.6486 15.999 14.245V14.82C15.999 15.714 15.679 16.579 15.099 17.258C13.529 19.091 11.142 19.996 7.996 19.996C4.85 19.996 2.464 19.091 0.898 17.256C0.318608 16.5774 0.000207249 15.7143 0 14.822V14.244C0.000265004 13.6476 0.237298 13.0757 0.659009 12.654C1.08072 12.2323 1.65261 11.9953 2.249 11.995H13.751ZM13.751 13.495H2.248C2.04909 13.495 1.85832 13.574 1.71767 13.7147C1.57702 13.8553 1.498 14.0461 1.498 14.245V14.822C1.498 15.357 1.69 15.875 2.038 16.282C3.291 17.751 5.258 18.496 7.995 18.496C10.734 18.496 12.701 17.751 13.958 16.283C14.3066 15.8753 14.4982 15.3564 14.498 14.82V14.244C14.4977 14.0456 14.4189 13.8554 14.2787 13.715C14.1385 13.5746 13.9494 13.4955 13.751 13.495ZM7.996 0C8.65261 -9.78424e-09 9.30279 0.129329 9.90942 0.380602C10.516 0.631876 11.0672 1.00017 11.5315 1.46447C11.9958 1.92876 12.3641 2.47995 12.6154 3.08658C12.8667 3.69321 12.996 4.34339 12.996 5C12.996 5.65661 12.8667 6.30679 12.6154 6.91342C12.3641 7.52004 11.9958 8.07124 11.5315 8.53553C11.0672 8.99983 10.516 9.36812 9.90942 9.6194C9.30279 9.87067 8.65261 10 7.996 10C6.66992 10 5.39815 9.47322 4.46047 8.53553C3.52278 7.59785 2.996 6.32608 2.996 5C2.996 3.67392 3.52278 2.40215 4.46047 1.46447C5.39815 0.526784 6.66992 0 7.996 0ZM7.996 1.5C7.53637 1.5 7.08125 1.59053 6.65661 1.76642C6.23197 1.94231 5.84613 2.20012 5.52113 2.52513C5.19612 2.85013 4.93831 3.23597 4.76242 3.66061C4.58653 4.08525 4.496 4.54037 4.496 5C4.496 5.45963 4.58653 5.91475 4.76242 6.33939C4.93831 6.76403 5.19612 7.14987 5.52113 7.47487C5.84613 7.79988 6.23197 8.05769 6.65661 8.23358C7.08125 8.40947 7.53637 8.5 7.996 8.5C8.92426 8.5 9.8145 8.13125 10.4709 7.47487C11.1273 6.8185 11.496 5.92826 11.496 5C11.496 4.07174 11.1273 3.1815 10.4709 2.52513C9.8145 1.86875 8.92426 1.5 7.996 1.5Z" fill="currentColor" />
        </svg>
    );

    const SetupIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M19.4 12.4a1.65 1.65 0 0 0 0-1.8l2.1-1.6a.5.5 0 0 0-.1-.7l-2-3.5a.5.5 0 0 0-.6-.2l-2.5 1a1.7 1.7 0 0 0-1.5-.9l-.4-2.7a.5.5 0 0 0-.5-.4h-4a.5.5 0 0 0-.5.4l-.4 2.7a1.7 1.7 0 0 0-1.5.9l-2.5-1a.5.5 0 0 0-.6.2l-2 3.5a.5.5 0 0 0 .1.7l2.1 1.6a1.65 1.65 0 0 0 0 1.8l-2.1 1.6a.5.5 0 0 0-.1.7l2 3.5c.1.2.3.3.6.2l2.5-1a1.7 1.7 0 0 0 1.5.9l.4 2.7a.5.5 0 0 0 .5.4h4a.5.5 0 0 0 .5-.4l.4-2.7a1.7 1.7 0 0 0 1.5-.9l2.5 1a.5.5 0 0 0 .6-.2l2-3.5a.5.5 0 0 0-.1-.7l-2.1-1.6z"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const UsersIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M16 21V19C16 17.9391 15.5786 16.9217 14.8284 16.1716C14.0783 15.4214 13.0609 15 12 15H6C4.93913 15 3.92172 15.4214 3.17157 16.1716C2.42143 16.9217 2 17.9391 2 19V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 11C11.2091 11 13 9.20914 13 7C13 4.79086 11.2091 3 9 3C6.79086 3 5 4.79086 5 7C5 9.20914 6.79086 11 9 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M22 21V19C21.9993 18.1137 21.7044 17.2528 21.1614 16.5523C20.6184 15.8519 19.8581 15.3516 19 15.13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M16 3.13C16.8604 3.35031 17.623 3.85071 18.1676 4.55232C18.7122 5.25392 19.0078 6.11683 19.0078 7.005C19.0078 7.89317 18.7122 8.75608 18.1676 9.45768C17.623 10.1593 16.8604 10.6597 16 10.88" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
    );

    const HQUsersIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M3 21H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <path d="M5 21V5C5 3.89543 5.89543 3 7 3H13C14.1046 3 15 3.89543 15 5V21" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M15 9H17C18.1046 9 19 9.89543 19 11V21" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
            <path d="M8.5 7H11.5M8.5 11H11.5M8.5 15H11.5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
        </svg>
    );

    const ComplaintIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9 5H7C6.46957 5 5.96086 5.21071 5.58579 5.58579C5.21071 5.96086 5 6.46957 5 7V19C5 19.5304 5.21071 20.0391 5.58579 20.4142C5.96086 20.7893 6.46957 21 7 21H17C17.5304 21 18.0391 20.7893 18.4142 20.4142C18.7893 20.0391 19 19.5304 19 19V7C19 6.46957 18.7893 5.96086 18.4142 5.58579C18.0391 5.21071 17.5304 5 17 5H15"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M9 5C9 4.46957 9.21071 3.96086 9.58579 3.58579C9.96086 3.21071 10.4696 3 11 3H13C13.5304 3 14.0391 3.21071 14.4142 3.58579C14.7893 3.96086 15 4.46957 15 5C15 5.53043 14.7893 6.03914 14.4142 6.41421C14.0391 6.78929 13.5304 7 13 7H11C10.4696 7 9.96086 6.78929 9.58579 6.41421C9.21071 6.03914 9 5.53043 9 5Z"
                stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            <path d="M12 11V13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            <circle cx="12" cy="16" r="0.5" fill="currentColor" stroke="currentColor" strokeWidth="1.5" />
        </svg>
    );

    const LogoutIcon = () => (
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M5.85 17.1C6.7 16.45 7.65 15.9375 8.7 15.5625C9.75 15.1875 10.85 15 12 15C13.15 15 14.25 15.1875 15.3 15.5625C16.35 15.9375 17.3 16.45 18.15 17.1C18.7333 16.4167 19.1875 15.6417 19.5125 14.775C19.8375 13.9083 20 12.9833 20 12C20 9.78333 19.2208 7.89583 17.6625 6.3375C16.1042 4.77917 14.2167 4 12 4C9.78333 4 7.89583 4.77917 6.3375 6.3375C4.77917 7.89583 4 9.78333 4 12C4 12.9833 4.1625 13.9083 4.4875 14.775C4.8125 15.6417 5.26667 16.4167 5.85 17.1ZM12 13C11.0167 13 10.1875 12.6625 9.5125 11.9875C8.8375 11.3125 8.5 10.4833 8.5 9.5C8.5 8.51667 8.8375 7.6875 9.5125 7.0125C10.1875 6.3375 11.0167 6 12 6C12.9833 6 13.8125 6.3375 14.4875 7.0125C15.1625 7.6875 15.5 8.51667 15.5 9.5C15.5 10.4833 15.1625 11.3125 14.4875 11.9875C13.8125 12.6625 12.9833 13 12 13ZM12 22C10.6167 22 9.31667 21.7375 8.1 21.2125C6.88333 20.6875 5.825 19.975 4.925 19.075C4.025 18.175 3.3125 17.1167 2.7875 15.9C2.2625 14.6833 2 13.3833 2 12C2 10.6167 2.2625 9.31667 2.7875 8.1C3.3125 6.88333 4.025 5.825 4.925 4.925C5.825 4.025 6.88333 3.3125 8.1 2.7875C9.31667 2.2625 10.6167 2 12 2C13.3833 2 14.6833 2.2625 15.9 2.7875C17.1167 3.3125 18.175 4.025 19.075 4.925C19.975 5.825 20.6875 6.88333 21.2125 8.1C21.7375 9.31667 22 10.6167 22 12C22 13.3833 21.7375 14.6833 21.2125 15.9C20.6875 17.1167 19.975 18.175 19.075 19.075C18.175 19.975 17.1167 20.6875 15.9 21.2125C14.6833 21.7375 13.3833 22 12 22Z" fill="currentColor" />
        </svg>
    );

    return (
        <nav
            className={`vh-100 position-fixed d-flex flex-column justify-content-between ${css.navbar} ${isCollapsed ? css.collapsed : css.expanded}`}
            style={{ width: isCollapsed ? COLLAPSED_WIDTH : EXPANDED_WIDTH }}
        >
            {/* Top Section */}
            <div>
                {/* Logo & Toggle Header */}
                <div
                    className={`pb-2 pt-3 px-3 d-flex align-items-center ${isCollapsed ? 'justify-content-center' : 'justify-content-between'}`}
                    style={{
                        overflow: 'hidden',
                        borderBottom: '1px solid var(--hairline)',
                        marginBottom: '8px'
                    }}
                >
                    <NavLink to={getLogoRedirectPath()}>
                        <img
                            src={companyLogo}
                            style={{
                                width: isCollapsed ? "35px" : "100px",
                                transition: "width 0.3s ease-in-out"
                            }}
                            alt="Company Logo"
                        />
                    </NavLink>
                    {renderToggleButton()}
                </div>

                {/* Navigation Items */}
                <div className='mt-2 d-flex flex-column align-items-center'>
                    {isPolicySection() ? (
                        <>
                            {/* Policy Library */}
                            {renderNavItem(
                                "/policies",
                                <PolicyIcon />,
                                "Policy Library",
                                () => currentPath === '/policies' || currentPath.startsWith('/details')
                            )}

                            {/* Marketing (conditional - requires URL and role access) */}
                            {isMarketingEnabled() && hasMarketingAccess() && renderNavItem(
                                "/marketingcompliance",
                                <MarketingIcon />,
                                "Marketing",
                                () => currentPath === '/marketingcompliance' || currentPath.startsWith('/marketing')
                            )}

                            {/* Assignments */}
                            {renderNavItem(
                                "/assignments",
                                <AssignmentIcon />,
                                "Assignments",
                                () => currentPath === '/assignments' || currentPath.startsWith('/assignment')
                            )}
                            {/* Complaints */}
                            {!isLoading && hasComplaints && renderNavItem(
                                "/complaints",
                                <ComplaintIcon />,
                                "Complaints"
                            )}
                        </>
                    ) : (
                        <>
                            {/* Vendor/Questionnaires */}
                            {renderNavItem(
                                "/vendors",
                                <VendorIcon />,
                                "Questionnaires",
                                () => currentPath === '/vendors' || currentPath === '/questions'
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Bottom Section */}
            <div className='mb-2'>
                {/* Setup (conditional - requires role access and specific paths) */}
                {hasMarketingAccess() &&
                    (currentPath.startsWith('/marketing') ||
                        currentPath.startsWith('/setup') ||
                        currentPath.startsWith('/adminpanel') ||
                        currentPath.startsWith('/internal-users')) &&
                    renderNavItem(
                        "/setup",
                        <SetupIcon />,
                        "Setup",
                        () => ['/setup', '/setup/', '/setup/archived'].includes(currentPath)
                    )
                }

                {canManageUsers() && isPolicySection() &&
                    renderNavItem(
                        "/internal-users",
                        <UsersIcon />,
                        "Users",
                        () => currentPath === '/internal-users' || currentPath === '/adminpanel'
                    )
                }

                {userTeam === 'superAdmin' && isHqPortal() && isPolicySection() &&
                    renderNavItem(
                        "/admin/hq-users",
                        <HQUsersIcon />,
                        "HQ Users",
                        () => currentPath === '/admin/hq-users'
                    )
                }

                {userTeam === 'superAdmin' && isHqPortal() && isPolicySection() &&
                    renderNavItem(
                        "/admin/onboard-customer",
                        <HQUsersIcon />,
                        "Onboard Customer",
                        () => currentPath === '/admin/onboard-customer'
                    )
                }

                {/* Logout */}
                <div
                    className={`d-flex align-items-center my-1 ${css.navItem} ${css.logoutItem} ${isCollapsed ? css.collapsed : ''}`}
                    onClick={handleLogOut}
                    style={{
                        cursor: 'pointer',
                        padding: isCollapsed ? '12px 8px' : '12px 14px',
                        borderRadius: '4px',
                        transition: 'all 0.2s ease'
                    }}
                >
                    <div className={css.iconContainer} style={{ paddingLeft: isCollapsed ? '0px' : "" }}>
                        <LogoutIcon />
                    </div>
                    <span
                        className={`${css.navText} ${isCollapsed ? css.hidden : css.visible}`}
                        style={{
                            marginLeft: isCollapsed ? '0' : '12px',
                            opacity: isCollapsed ? 0 : 1,
                            transform: isCollapsed ? 'translateX(-10px)' : 'translateX(0)',
                            transition: 'all 0.3s ease',
                            whiteSpace: 'nowrap',
                            fontSize: '14px',
                            fontWeight: '500'
                        }}
                    >
                        Logout
                    </span>
                </div>
            </div>
        </nav>
    );
}
